"use client";

import { type RefObject, useEffect, useMemo, useRef } from "react";

/**
 * What the dismissing gesture does to the control it landed on.
 *
 * `"pass-through"` is the platform norm (native popover light-dismiss): the
 * tap closes the overlay *and* activates whatever was under it. Use
 * `"consume"` where the open overlay sits over or beside controls that would
 * be costly to trigger by accident — the dismissal then swallows the
 * activation too, so the gesture only closes.
 */
export type DismissBehavior = "pass-through" | "consume";

export interface DismissOptions {
  /** Default `"pass-through"`. */
  behavior?: DismissBehavior;
  /** Dismiss on Escape as well. Default true. */
  escape?: boolean;
  /** Return true for an outside target that should *not* dismiss. Must be stable. */
  ignore?: (target: Element) => boolean;
}

/**
 * What every currently open dismiss scope counts as inside itself. A consumed
 * dismissal reads this to tell a stray gesture from one that belongs to an
 * overlay in front of it: overlays have no shared z-order to consult, but the
 * one the gesture landed in has said as much by registering it.
 */
const openScopes = new Set<(target: Element) => boolean>();

function claimedByAnotherScope(self: (target: Element) => boolean, target: Element) {
  for (const scope of openScopes) {
    if (scope !== self && scope(target)) return true;
  }
  return false;
}

// preventDefault on pointerdown does not suppress the click that follows, so
// consuming a gesture means swallowing that click itself. The swallower
// deliberately outlives the effect that installed it — the dismissal it
// belongs to has already unmounted or re-rendered by the time the click lands.
// It releases on that click, or on the next gesture if the pointer is dragged
// away and no click ever arrives, so it can never eat a later one. A keydown
// releases it too: a gesture that ends with neither a click nor a cancel would
// otherwise leave it armed, and the click Enter synthesizes on some focused
// control is not the one this dismissal was owed.
function consumeActivation(source: Event) {
  const swallow = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    release();
  };
  const restart = (event: Event) => {
    if (event !== source) release();
  };
  const release = () => {
    window.removeEventListener("click", swallow, true);
    window.removeEventListener("pointerdown", restart, true);
    window.removeEventListener("pointercancel", restart, true);
    window.removeEventListener("keydown", release, true);
  };
  window.addEventListener("click", swallow, true);
  window.addEventListener("pointerdown", restart, true);
  window.addEventListener("pointercancel", restart, true);
  window.addEventListener("keydown", release, true);
}

/**
 * Close an open overlay on Escape or a pointerdown outside `ref`. Pass `null`
 * for `ref` when what counts as inside isn't one element, and say so with
 * `ignore` instead.
 *
 * The pointerdown listener is capture-phase: a bubble-phase one is blinded by
 * any handler in between that stops propagation, and an overlay cannot know
 * what it is layered over. `onDismiss` and `ignore` must be stable (wrap in
 * useCallback) so the listeners aren't re-bound every render while open.
 */
export function useDismiss(
  open: boolean,
  onDismiss: () => void,
  ref: RefObject<HTMLElement | SVGElement | null> | null,
  { behavior = "pass-through", escape: dismissOnEscape = true, ignore }: DismissOptions = {},
) {
  useEffect(() => {
    if (!open) return;
    const inside = (target: Element) =>
      Boolean(ref?.current?.contains(target)) || Boolean(ignore?.(target));
    const onKey = (event: KeyboardEvent) => {
      if (dismissOnEscape && event.key === "Escape") onDismiss();
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target || inside(target)) return;
      // Outside this overlay, but inside one that is also open: the gesture is
      // that overlay's to answer, and swallowing its click from behind would
      // cost the user the control they actually aimed at.
      if (behavior === "consume" && !claimedByAnotherScope(inside, target)) {
        consumeActivation(event);
      }
      onDismiss();
    };
    openScopes.add(inside);
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer, true);
    return () => {
      openScopes.delete(inside);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer, true);
    };
  }, [open, onDismiss, ref, behavior, dismissOnEscape, ignore]);
}

/* ── Bavimail additions: layered floating content ─────────────────────────
 * Menus and popovers sit on top of drawers and modals that also listen for
 * Escape and outside pointers, and may themselves hold floating lists that are
 * portalled to <body> (the beUI combobox list, say). The helpers below give
 * every floating layer the same two answers: "is a nested floating layer open"
 * and "am I the topmost layer that should react to Escape". */

/**
 * Floating content that is portalled out of a popover/menu's DOM subtree but
 * still belongs to it. Give such content `data-popover-ignore` (or
 * `data-floating`); the beUI combobox list is matched by its own attribute.
 * A pointer landing inside a match never dismisses the layer beneath it, and
 * Escape goes to the match first while it is visibly open.
 */
export const FLOATING_IGNORE_SELECTOR =
  "[data-popover-ignore], [data-floating], [data-combobox-content]";

/** Whether `target` sits inside an element matched by `selector`. */
export function isInsideFloating(target: Element, selector: string) {
  return Boolean(target.closest(selector));
}

/**
 * Whether an element matched by `selector` is currently open: mounted,
 * neither `inert` nor `aria-hidden`, and painted. Elements that are, or that
 * contain, `except` are skipped — those are the layer asking, or a layer it
 * sits inside, not a nested one.
 */
export function isFloatingLayerOpen(selector: string, except?: Element | null) {
  for (const element of document.querySelectorAll<HTMLElement>(selector)) {
    if (except && (element === except || element.contains(except))) continue;
    if (element.hasAttribute("inert")) continue;
    if (element.getAttribute("aria-hidden") === "true") continue;
    if (element.getClientRects().length === 0) continue;
    return true;
  }
  return false;
}

/**
 * Outside-pointer dismissal for a layer that may hold nested portals.
 *
 * Spread the returned props on the layer's React root. React events bubble
 * through portals along the *React* tree, so a pointerdown inside anything the
 * layer rendered — however far it was portalled — passes that root's capture
 * handler and is marked as inside, with no selector to keep in sync. The
 * deciding listener is therefore bubble-phase on `document`, after React has
 * dispatched; a capture-phase window listener clears the mark first so a
 * gesture that never reached `document` cannot leave it set.
 *
 * `isInside` answers for what the React tree cannot: the trigger, and floating
 * content owned by another root (`isInsideFloating`). `onOutside` and
 * `isInside` must be stable (useCallback).
 */
export function useOutsidePointer(
  open: boolean,
  onOutside: () => void,
  isInside: (target: Element) => boolean,
) {
  const landedInside = useRef(false);

  useEffect(() => {
    if (!open) return;
    const reset = () => {
      landedInside.current = false;
    };
    const onPointer = (event: PointerEvent) => {
      const inside = landedInside.current;
      landedInside.current = false;
      const target = event.target instanceof Element ? event.target : null;
      if (inside || !target || isInside(target)) return;
      onOutside();
    };
    window.addEventListener("pointerdown", reset, true);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("pointerdown", reset, true);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, onOutside, isInside]);

  return useMemo(
    () => ({
      onPointerDownCapture: () => {
        landedInside.current = true;
      },
    }),
    [],
  );
}

const escapeLayers: symbol[] = [];

export interface LayeredEscapeOptions {
  /**
   * Nested floating content that should answer Escape before this layer does.
   * Default `FLOATING_IGNORE_SELECTOR`.
   */
  nestedSelector?: string;
  /** This layer's own root; matches of `nestedSelector` containing it are not nested. */
  self?: RefObject<Element | null>;
}

/**
 * Escape for stacked floating layers. While `open`, the layer joins a stack;
 * only the topmost open layer reacts, and when it does the event is stopped so
 * overlays beneath it (a Drawer, a Modal, a page-level shortcut) never see it.
 *
 * Two cases hand Escape on instead: an event a nested control has already
 * `preventDefault`ed (the combobox input closing its own list) is stopped but
 * not acted on, and a nested floating layer that is visibly open but has not
 * handled the event yet (it listens on `window`, after us) is left alone so it
 * can close itself. The listener is bubble-phase on `document`, which is where
 * React dispatches, so every React handler inside the layer has run first.
 *
 * `onEscape` must be stable (useCallback).
 */
export function useLayeredEscape(
  open: boolean,
  onEscape: () => void,
  { nestedSelector = FLOATING_IGNORE_SELECTOR, self }: LayeredEscapeOptions = {},
) {
  useEffect(() => {
    if (!open) return;
    const token = Symbol("escape-layer");
    escapeLayers.push(token);

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (escapeLayers[escapeLayers.length - 1] !== token) return;
      if (event.defaultPrevented) {
        event.stopPropagation();
        return;
      }
      if (isFloatingLayerOpen(nestedSelector, self?.current)) return;
      event.preventDefault();
      event.stopPropagation();
      onEscape();
    };

    document.addEventListener("keydown", onKey);
    return () => {
      const index = escapeLayers.lastIndexOf(token);
      if (index >= 0) escapeLayers.splice(index, 1);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onEscape, nestedSelector, self]);
}
